import type { AnimationEvent } from '@angular/animations';
import { Dir } from '@angular/cdk/bidi';
import type { OnChanges, SimpleChanges, TemplateRef } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Optional,
  Output,
  output,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';

import { BehaviorSubject } from 'rxjs';

import type { UnwrapInputs } from '@core/common-legacy';
import { macroTask } from '@core/utils-legacy';

import { RtTooltipInvalidPositionError } from './tooltip-errors';
import type { TooltipPosition, TooltipType, TooltipVisibility } from './tooltip-inner.component';
import { TooltipInnerComponent } from './tooltip-inner.component';

@Component({
  selector: 'rt-tooltip',
  template: `
    <rt-tooltip-inner
      [message]="message()"
      [templateRef]="templateRef()"
      [type]="type()"
      [hasBorder]="hasBorder()"
      [hasShadow]="hasShadow()"
      [hasNoArrow]="hasNoArrow()"
      [hasTextAlignCenter]="hasTextAlignCenter()"
      [visibility]="visibility"
      [position]="position"
      [transformOrigin]="transformOrigin"
      [arrowOffset]="arrowOffset()"
      [style.max-width.px]="maxWidth()"
      [style.white-space]="wordWrap()"
      [skipTransition]="skipTransition()"
      (hide)="onTooltipHide()"
      (animationState)="animationState.next($event)"
    />
  `,
  host: {
    '(body:click)': 'this.handleBodyInteraction()',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TooltipInnerComponent],
})
export class TooltipComponent implements OnChanges {
  public readonly message = input<string>(undefined!);
  public readonly templateRef = input<TemplateRef<any> | null>(null);
  public readonly type = input<TooltipType>('default');
  public readonly hasBorder = input<boolean>(undefined!);
  public readonly hasShadow = input<boolean>(undefined!);
  public readonly hasNoArrow = input<boolean>(undefined!);
  public readonly hasTextAlignCenter = input<boolean>(undefined!);
  public readonly maxWidth = input<number>(300);
  public readonly wordWrap = input<boolean | string>(undefined!);
  public readonly skipTransition = input<boolean>(false);
  public readonly arrowOffset = input<{
    offsetX: number;
    offsetY: number;
  }>();

  /** Property watched by the animation framework to show or hide the tooltip */
  @Input() public visibility: TooltipVisibility = 'initial';

  @Output() public visibilityChange = new EventEmitter<TooltipVisibility>();

  /** Subject for notifying that the tooltip has been hidden from the view */
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  public onHide = output<void>();

  public position: TooltipPosition;
  public whiteSpace: string;
  public animationState = new BehaviorSubject<AnimationEvent | null>(null);

  /** The transform origin used in the animation for showing and hiding the tooltip */
  public transformOrigin: string = 'above';

  constructor(
    @Optional() private dir: Dir,
    public cdRef: ChangeDetectorRef,
  ) {
    this.cdRef.detach();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('wordWrap' in changes) {
      const wordWrap = this.wordWrap();
      this.whiteSpace = typeof wordWrap === 'boolean' ? (wordWrap ? 'normal' : 'nowrap') : wordWrap;
    }
    macroTask(() => this.cdRef.detectChanges());
  }

  /**
   * Shows the tooltip with an animation originating from the provided origin
   * @param position Position of the tooltip.
   */
  public show(): void {
    this.visibility = 'visible';

    this.visibilityChange.emit(this.visibility);
    this.cdRef.detectChanges();
  }

  /**
   * Begins the animation to hide the tooltip after the provided delay in ms.
   */
  public hide(): void {
    this.visibility = 'hidden';
    this.visibilityChange.emit(this.visibility);
    this.cdRef.detectChanges();
  }

  public onTooltipHide(): void {
    this.onHide.emit();
  }

  /**
   * Interactions on the HTML body should close the tooltip immediately as defined in the
   * material design spec.
   * https://material.google.com/components/tooltips.html#tooltips-interaction
   */
  public handleBodyInteraction(): void {
    this.hide();
  }

  /** Sets the tooltip transform origin according to the tooltip position */
  public setTooltipPosition(value: TooltipPosition): void {
    const isLtr = !this.dir || this.dir.value === 'ltr';

    switch (value) {
      case 'before':
        this.transformOrigin = isLtr ? 'right' : 'left';
        break;
      case 'after':
        this.transformOrigin = isLtr ? 'left' : 'right';
        break;
      case 'left':
      case 'leftbelow':
        this.transformOrigin = 'right';
        break;
      case 'right':
      case 'aboveright':
      case 'righttop':
      case 'rightbelow':
        this.transformOrigin = 'left';
        break;
      case 'above':
        this.transformOrigin = 'bottom';
        break;
      case 'below':
        this.transformOrigin = 'top';
        break;
      default:
        throw new RtTooltipInvalidPositionError(value);
    }

    this.position = value;
  }
}

export type TooltipInputs = UnwrapInputs<
  TooltipComponent,
  | 'templateRef'
  | 'message'
  | 'type'
  | 'maxWidth'
  | 'wordWrap'
  | 'hasBorder'
  | 'hasShadow'
  | 'hasNoArrow'
  | 'hasTextAlignCenter'
  | 'skipTransition'
  | 'arrowOffset'
>;

export const TooltipComponentInputKeys: Array<keyof TooltipInputs> = [
  'type',
  'maxWidth',
  'wordWrap',
  'hasBorder',
  'hasShadow',
  'hasNoArrow',
  'message',
  'templateRef',
  'hasTextAlignCenter',
  'skipTransition',
  'arrowOffset',
];
